/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'

import {
  Table,
  Spinner,
  PageNavigationButtons,
  CenteredContainerDisplay,
  SpanWithData
} from '../'

import {
  PageNavigationControl,
  getAvatarInitialsColour,
  decodeBase64,
  saveCursor,
  getPageCursor
} from '../../utils'

class Tasks extends React.Component {
  static propTypes = {
    tasks: PropTypes.object,
    status: PropTypes.string,
    workflowRunStatus: PropTypes.string,
    workflow: PropTypes.string,
    assignedTo: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }

  static defaultProps = {
    resultsPerPage: 15
  }

  state = {
    pageNav: new PageNavigationControl(),
    filterChanged: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ((this.props.status !== nextProps.status ||
      this.props.workflowRunStatus !== nextProps.workflowRunStatus ||
      this.props.workflow !== nextProps.workflow ||
      this.props.assignedTo !== nextProps.assignedTo ||
      this.props.startDate !== nextProps.startDate ||
      this.props.endDate !== nextProps.endDate) &&
      !sessionStorage.getItem("taskListPageState")
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem("taskListFilters"))
      if (previousFilters && (
        previousFilters.status === nextProps.status ||
        previousFilters.workflowRunStatus === nextProps.workflowRunStatus ||
        previousFilters.workflow === nextProps.workflow ||
        previousFilters.assignedTo === nextProps.assignedTo ||
        previousFilters.startDate === nextProps.startDate ||
        previousFilters.endDate === nextProps.endDate
      )) {
        sessionStorage.removeItem("taskListFilters")
      } else {
        this.state.pageNav.reset()
        this.setState({
          filterChanged: true
        }, () => this.props.refetch())
        sessionStorage.removeItem("taskListFilters")
      }
    } else {
      if (this.state.filterChanged) {
        this.setState({
          filterChanged: false
        })
        this.state.pageNav.setPageStates(null, nextProps.tasks)
      }
      else if (nextProps.tasks) {
        let taskListPageState = JSON.parse(sessionStorage.getItem("taskListPageState"))
        if (taskListPageState) {
          this.state.pageNav.setPageStates(null, null, taskListPageState)
          sessionStorage.removeItem("taskListPageState")
          this.props.refetch({ cursor: taskListPageState.cursor })
        } else {
          this.state.pageNav.setPageStates(this.props.tasks, nextProps.tasks)
        }
      }
    }
  }

  savePaginationAndFilterState = (startCursor) => {
    sessionStorage.setItem("taskListPageState", JSON.stringify({
      firstPage: this.state.pageNav.firstPage,
      lastPage: this.state.pageNav.lastPage,
      currentPage: this.state.pageNav.currentPage,
      pages: this.state.pageNav.pages,
      cursor: saveCursor(startCursor)
    }))
    sessionStorage.setItem("taskListFilters", JSON.stringify({
      status: this.props.status,
      workflowRunStatus: this.props.workflowRunStatus,
      workflow: this.props.workflow,
      assignedTo: this.props.assignedTo,
      startDate: this.props.startDate,
      endDate: this.props.endDate
    }))
  }

  saveStateAndGoToDetailPage = (workflowRunId, workflowRunStepId, startCursor) => {
    this.savePaginationAndFilterState(startCursor)
    this.props.history.push(`/os/operations/general/workflows/${workflowRunId}/${workflowRunStepId}`)
  }

  render() {
    if (this.props.loading) {
      return (
        <CenteredContainerDisplay
          content={
            <Spinner className="slds-is-relative" />  // TODO: loading state stencil instead
          }
          yAdjustPercentage={-15}
        />
      )
    }

    if (this.props.error) {
      return <div>Failed to load tasks</div>  // TODO: improve error response?
    }

    if (this.props.tasks.edges.length === 0) {
      return <div>{`No ${this.props.status} task records to display`}</div>
    }

    let pageNav = this.state.pageNav
    let tableColumns = ["Workflow", "Run", "Step", "Step name", "Status", "Assigned to"]

    let tasks = this.props.tasks
    let startCursor = tasks.pageInfo.startCursor
    let totalCount = tasks.totalCount

    return (
      <div className="slds-scrollable_x">
        <Table
          columns={tableColumns}
          rows={
            tasks.edges.map((task, index) => (
              <TaskDetailsRow
                workflowRunStep={task.node}
                onClick={(workflowRunId, workflowRunStepId) => this.saveStateAndGoToDetailPage(workflowRunId, workflowRunStepId, startCursor)}
                key={`caseDetailsRow_${index}`}
              />
            ))
          }
          objectRows={true}
        />
        <PageNavigationButtons
          onNextClick={() => pageNav.nextPage(this.props.loadNextTasks)}
          onPreviousClick={() => pageNav.previousPage(this.props.loadPreviousTasks)}
          previousDisabled={pageNav.firstPage}
          nextDisabled={pageNav.lastPage}
          startCursor={startCursor}
          totalCount={totalCount}
          resultsPerPage={this.props.resultsPerPage}
          resultsText='tasks'
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
          className="slds-m-top_small slds-clearfix slds-float_right"
        />
      </div>
    )
  }
}

const TaskDetailsRow = (props) => {
  let workflowRunStep = props.workflowRunStep

  let name = workflowRunStep.assignedTo
    ? workflowRunStep.assignedTo.firstName + ' ' + workflowRunStep.assignedTo.lastName
    : null
  let initials = name
    ? `${workflowRunStep.assignedTo.firstName[0]}${workflowRunStep.assignedTo.lastName[0]}`
    : null
  let avatarInitialsColour = initials
    ? getAvatarInitialsColour(initials)
    : null

  return (
    <tr className="task-table-row" onClick={() => props.onClick(workflowRunStep.workflowrun.id, workflowRunStep.id)}>
      <th scope="row" data-label="Workflow">
        <div className="slds-truncate" title="Workflow name">
          {workflowRunStep.workflowrun.workflow.name}
        </div>
      </th>
      <td data-label="Workflow run">
        <div className="slds-truncate" title="Run">
          {workflowRunStep.workflowrun.description}
        </div>
      </td>
      <td data-label="Workflow at step">
        <div className="slds-truncate" title="Step">
          {workflowRunStep.workflowrun.atStep}
        </div>
      </td>
      <td data-label="Workflow step">
        <div className="slds-truncate" title="Step name">
          {workflowRunStep.workflowstep.name}
        </div>
      </td>
      <td data-label="Status">
        <div className="slds-truncate" title="Status">
          <SpanWithData forField="WorkflowRunStepStatus" searchKey={workflowRunStep.status} />
        </div>
      </td>
      <td data-label="Assigned to">
        <div className="slds-truncate" title="Assigned to">
          <div className="case-assignee">
            {name &&
              <div className="slds-avatar slds-avatar_small slds-avatar_circle slds-m-right_x-small">
                <abbr
                  className={`slds-avatar__initials slds-icon-standard-user ${avatarInitialsColour ? `avatar-color_${avatarInitialsColour}` : ''}`}
                  title={name}>
                  {initials}
                </abbr>
              </div>
            }
            <div className="text-follows-icon case-assignee_name">
              {name}
            </div>
          </div>
        </div>
      </td>
    </tr>
  )
}

const getTasks = gql`
  query($cursor: String, $search: String, $status: String!, $workflow: UUID, $assignedTo: UUID, $workflowRunStatus: String!, $startDate: DateTime, $endDate: DateTime, $resultsPerPage: Int!) {
    tasks: workflowrunsteps(first: $resultsPerPage, after: $cursor, workflowstep_ExecutionMethod: "manual", orderBy: "scheduled_at", workflowrun_Description_Icontains: $search, status: $status, workflowrun_Workflow_Id: $workflow, assignedTo_Id: $assignedTo, workflowrun_Status: $workflowRunStatus, scheduledOnOrAfter: $startDate, scheduledOnOrBefore: $endDate) {
      totalCount
      edges {
        node {
          id
          status
          description
          assignedTo {
            id
            firstName
            lastName
          }
          parameters
          createdAt
          scheduledAt
          workflowrun {
            id
            description
            status
            atStep
            workflow {
              id
              name
            }
          }
          workflowstep {
            id
            name
            number
            executionMethod
            responsibility
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousTasks = gql`
  query($cursor: String, $search: String, $status: String!, $workflow: UUID, $assignedTo: UUID, $workflowRunStatus: String!, $startDate: DateTime, $endDate: DateTime, $resultsPerPage: Int!) {
    tasks: workflowrunsteps(last: $resultsPerPage, before: $cursor, workflowstep_ExecutionMethod: "manual", orderBy: "scheduled_at", workflowrun_Description_Icontains: $search, status: $status, workflowrun_Workflow_Id: $workflow, assignedTo_Id: $assignedTo, workflowrun_Status: $workflowRunStatus, scheduledOnOrAfter: $startDate, scheduledOnOrBefore: $endDate) {
      totalCount
      edges {
        node {
          id
          status
          description
          assignedTo {
            id
            firstName
            lastName
          }
          parameters
          createdAt
          scheduledAt
          workflowrun {
            id
            description
            status
            atStep
            workflow {
              id
              name
            }
          }
          workflowstep {
            id
            name
            number
            executionMethod
            responsibility
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData = graphql(getTasks, {
    options: ({ search, status, workflowRunStatus, workflow, assignedTo, startDate, endDate, resultsPerPage = 15, cursor }) => ({
      variables: {
        search,
        status: status || "ready",
        workflowRunStatus: workflowRunStatus || "processed",
        workflow: workflow ? decodeBase64(workflow) : undefined,
        assignedTo: assignedTo ? decodeBase64(assignedTo) : null,
        startDate: startDate,
        endDate: endDate,
        resultsPerPage,
        cursor: cursor
      }
    }),
    props: ({ ownProps: { search, status, workflowRunStatus, workflow, assignedTo, startDate, endDate, resultsPerPage = 15 }, data: { loading, error, refetch, tasks, fetchMore }}) => ({
      loading,
      error,
      refetch,
      tasks,
      loadNextTasks: () => {
        return fetchMore({
          query: getTasks,
          variables: {
            search,
            status: status || "ready",
            workflowRunStatus: workflowRunStatus || "processed",
            workflow: workflow ? decodeBase64(workflow) : undefined,
            assignedTo: assignedTo ? decodeBase64(assignedTo) : null,
            startDate,
            endDate,
            resultsPerPage,
            cursor: tasks.pageInfo.endCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.tasks.edges
            const pageInfo = fetchMoreResult.tasks.pageInfo

            return {
              tasks: {
                __typename: previousResult.tasks.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      loadPreviousTasks: () => {
        return fetchMore({
          query: getPreviousTasks,
          variables: {
            search,
            status: status || "ready",
            workflowRunStatus: workflowRunStatus || "processed",
            workflow: workflow ? decodeBase64(workflow) : undefined,
            assignedTo: assignedTo ? decodeBase64(assignedTo) : null,
            startDate,
            endDate,
            resultsPerPage,
            cursor: tasks.pageInfo.startCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.tasks.edges
            const pageInfo = fetchMoreResult.tasks.pageInfo

            return {
              tasks: {
                __typename: previousResult.tasks.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withData,
  withRouter
)(Tasks)



// WEBPACK FOOTER //
// ./src/components/tasks/index.js